import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationApi } from '../services/api';
import { notificationActionPath } from '../utils/notificationLinks';
import { localizeNotification } from '../utils/notificationText';

/** Notifications shown when the widget first loads. */
const INITIAL_COUNT = 3;
/** Additional notifications appended on each "Visa fler" click. */
const PAGE_SIZE = 5;

const COLOR_MAP: Record<string, string> = {
  primary: 'primary',
  secondary: 'secondary',
  success: 'success',
  danger: 'danger',
  warning: 'warning',
  info: 'info'
};

/**
 * Shared start-page notifications widget for all four personas (company,
 * person, lawyer, inspector). Shows the 3 latest notifications, newest first,
 * and progressively loads 5 more per "Visa fler" click via server-side keyset
 * pagination — so the start page stays uncluttered while the full history is
 * reachable without leaving the page. The global bell icon is unchanged and no
 * notification is ever removed from the system.
 *
 * An unread notification is marked read only when the user interacts with that
 * individual item (opens it), so the bell keeps lighting up until then.
 */
export function NotificationsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [items, setItems] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Fetch `count` notifications older than `cursor`. Uses a +1 probe: request
  // one extra so we can tell whether more remain without needing a total.
  const fetchPage = useCallback(async (count: number, cursor?: string) => {
    const res = await notificationApi.getMyNotifications({ limit: count + 1, cursor });
    return { page: res.slice(0, count), more: res.length > count };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { page, more } = await fetchPage(INITIAL_COUNT);
        if (cancelled) return;
        setItems(page);
        setHasMore(more);
      } catch {
        if (!cancelled) {
          setItems([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const handleLoadMore = async () => {
    if (loadingMore || items.length === 0) return;
    setLoadingMore(true);
    try {
      const cursor = items[items.length - 1]?.createdAt;
      const { page, more } = await fetchPage(PAGE_SIZE, cursor);
      setItems((prev) => [...prev, ...page]);
      setHasMore(more);
    } catch {
      /* keep the current list on failure */
    } finally {
      setLoadingMore(false);
    }
  };

  const handleItemClick = (n: any) => {
    const actionPath = notificationActionPath(n, user);
    if (!n.read) {
      notificationApi.markAsRead(n.id).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (actionPath) navigate(actionPath);
  };

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">
          <i className="feather-bell me-2" aria-hidden="true"></i>
          {t('notifications.title', 'Notifieringar')}
        </h5>
      </div>
      <div className="card-body custom-card-action p-0">
        {!loading && items.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="feather-bell-off fs-3 d-block mb-2"></i>
            <small>{t('notifications.noNotifications', 'Inga notifieringar ännu')}</small>
          </div>
        ) : (
          <>
            <ul className="list-unstyled mb-0 activity-feed-1" style={{ padding: '1rem 1.25rem' }}>
              {items.map((n) => {
                const feedColor = COLOR_MAP[n.metadata?.color] || 'primary';
                const { title, body } = localizeNotification(n, t);
                const actionPath = notificationActionPath(n, user);
                return (
                  <li key={n.id} className={`feed-item feed-item-${feedColor}`}>
                    <div
                      className="d-flex gap-3 justify-content-between"
                      style={{ cursor: actionPath ? 'pointer' : 'default' }}
                      onClick={() => handleItemClick(n)}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div className={`mb-1 text-truncate-1-line notification-title ${!n.read ? 'fw-bold' : ''}`}>
                          {title}
                        </div>
                        <p
                          className="fs-12 text-muted mb-0 text-truncate-2-line notification-body"
                          title={body}
                        >
                          {body}
                        </p>
                      </div>
                      <div className="fs-12 fw-medium text-uppercase text-muted text-nowrap flex-shrink-0">
                        {new Date(n.createdAt).toLocaleString('sv-SE', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            {hasMore && (
              <div className="px-3 pb-3 pt-1">
                <button
                  type="button"
                  className="btn btn-sm btn-light-primary w-100"
                  style={{ minHeight: 44 }}
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore
                    ? t('common.loading', 'Laddar…')
                    : t('notifications.showMore', 'Visa fler')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
